import { useAdminAnalytics } from '@/hooks/useAdminAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Users, FileText, DollarSign, Activity } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export const AnalyticsDashboard = () => {
  const { analytics, loading } = useAdminAnalytics();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: analytics.totalUsers,
      icon: Users,
      description: `${analytics.activeUsers} active in last 30 days`
    },
    {
      title: 'Total Content',
      value: analytics.totalContent,
      icon: FileText,
      description: 'Across all types'
    },
    {
      title: 'Total Revenue',
      value: `₹${analytics.totalRevenue.toLocaleString()}`,
      icon: DollarSign,
      description: 'From credit purchases'
    },
    {
      title: 'Active Rate',
      value: `${Math.round((analytics.activeUsers / analytics.totalUsers) * 100)}%`,
      icon: Activity,
      description: 'User engagement rate'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Content by Type */}
      <Card>
        <CardHeader>
          <CardTitle>Content by Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.contentByType.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium capitalize">{item.type}</span>
                  <span className="text-sm text-muted-foreground">{item.count}</span>
                </div>
                <Progress
                  value={(item.count / analytics.totalContent) * 100}
                  className="h-2"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users by Plan */}
      <Card>
        <CardHeader>
          <CardTitle>Users by Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.usersByPlan.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium capitalize">{item.plan}</span>
                  <span className="text-sm text-muted-foreground">{item.count}</span>
                </div>
                <Progress
                  value={(item.count / analytics.totalUsers) * 100}
                  className="h-2"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
